const DEVICE_ID_STORAGE_KEY = 'ns-hmrc-device-id';

/**
 * The values only the browser can supply for HMRC's fraud-prevention headers (connection method WEB_APP_VIA_SERVER,
 * spec v3.3). The lambda turns them into Gov-Client-Device-ID, -Timezone, -Screens, -Window-Size and
 * -Browser-JS-User-Agent, and adds what the server knows itself (the public IP and port, the vendor's public IP, and
 * Gov-Vendor-Product-Name and -Version, which are set server-side so a browser cannot name the software). Gov-Client-Browser-Plugins, -Do-Not-Track and -Local-IPs are not collected: HMRC
 * dropped them for this connection method (change log, 31 January 2022 and version 3.2).
 */
export interface HMRCDeviceInfo {
  deviceId: string;
  timezone: string;
  /** navigator.userAgent as the browser reports it: the source of Gov-Client-Browser-JS-User-Agent */
  userAgent: string;
  screen: {
    width: number;
    height: number;
    colourDepth: number;
    scalingFactor: number;
  };
  window: {
    width: number;
    height: number;
  };
}

/** The raw browser readings behind HMRCDeviceInfo, so the mapping can be tested without a DOM */
export interface BrowserDeviceReadings {
  deviceId: string;
  /** Minutes east of UTC, i.e. -Date.getTimezoneOffset() */
  utcOffsetMinutes: number;
  userAgent: string;
  screen: { width: number; height: number; colorDepth: number; devicePixelRatio: number };
  window: { innerWidth: number; innerHeight: number };
}

function getOrCreateDeviceId(): string {
  const existing = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
  if (existing) return existing;

  const deviceId = crypto.randomUUID();
  localStorage.setItem(DEVICE_ID_STORAGE_KEY, deviceId);
  return deviceId;
}

/**
 * Formats an offset in minutes east of UTC as HMRC's Gov-Client-Timezone value, UTC±hh:mm (UTC+00:00, UTC-01:15).
 */
export function formatTimezoneOffset(utcOffsetMinutes: number): string {
  const sign = utcOffsetMinutes >= 0 ? '+' : '-';
  const absMinutes = Math.abs(utcOffsetMinutes);
  const hours = String(Math.floor(absMinutes / 60)).padStart(2, '0');
  const minutes = String(absMinutes % 60).padStart(2, '0');
  return `UTC${sign}${hours}:${minutes}`;
}

/**
 * Maps raw browser readings to the device info the lambda expects. Pure: collectHMRCDeviceInfo() does the reading.
 */
export function buildHMRCDeviceInfo(readings: BrowserDeviceReadings): HMRCDeviceInfo {
  return {
    deviceId: readings.deviceId,
    timezone: formatTimezoneOffset(readings.utcOffsetMinutes),
    userAgent: readings.userAgent,
    screen: {
      width: readings.screen.width,
      height: readings.screen.height,
      colourDepth: readings.screen.colorDepth,
      scalingFactor: readings.screen.devicePixelRatio,
    },
    window: {
      width: readings.window.innerWidth,
      height: readings.window.innerHeight,
    },
  };
}

/**
 * Collects the values needed for HMRC's fraud-prevention headers that only the client can supply: browser/device data.
 * The product name and the web app's version are not sent: the lambda sets them (the install writes the client's version
 * to the environment secure store). The user agent is read here too, because HMRC wants the one JavaScript
 * reports on the device, not the User-Agent header the server sees. Values obtainable from the server-side request
 * itself (public IP and port) are added later by the lambda, not here.
 */
export function collectHMRCDeviceInfo(): HMRCDeviceInfo {
  return buildHMRCDeviceInfo({
    deviceId: getOrCreateDeviceId(),
    utcOffsetMinutes: -new Date().getTimezoneOffset(),
    userAgent: navigator.userAgent,
    screen: {
      width: window.screen.width,
      height: window.screen.height,
      colorDepth: window.screen.colorDepth,
      devicePixelRatio: window.devicePixelRatio,
    },
    window: {
      innerWidth: window.innerWidth,
      innerHeight: window.innerHeight,
    },
  });
}
